import {
  Home,
  MessageSquareText,
  ListChecks,
  ClipboardCheck,
  ClipboardList,
  Building2,
  Network,
  Folder,
  FileText,
  FolderOpen,
  Settings,
  UserCog,
} from "lucide-react";
import type { NavSection, NavItem } from "@skypointcloud/skypoint-ui";
import type { TermKey } from "../../../shared/products/performance/config";
import { BASE_PACK } from "../../../shared/products/performance/config";
import { PERFORMANCE_ROLE_NAMES } from "../../../shared/products/performance/roleNames";
import { inSentence } from "./wording";

/**
 * skyPerformance's sidebar (spec §6; wireframe `core.js:229-268`). Ids are stable keys the route
 * table and guard index on; labels come from the vocabulary. Phase 2 ships Home and Settings;
 * each Phase 3 epic appends its id to SHIPPED_FEATURES. E9 (operator ruling O-2): Settings is ONE
 * item, last in the list as the wireframe's HR nav (core.js:238-249). 2026-10-08 shell spec, D5: it
 * opens the tiled Settings page (`perf-settings`), and its entries, the tiles, are its children:
 * E9's tabbed page (`perf-config`) and the shared Users page, gated exactly as before. The drawer
 * draws the item without them (sidebar/settingsHub.ts).
 *
 * PURE: no store, no RTK, so it can be called and tested as a plain function. The hook that loads
 * the context lives in hooks/usePerformanceNavContext.ts. sidebarConfig.ts re-exports that hook,
 * so importing sidebarConfig (the nav snapshot test, HomeRoute, AppLayout, routeGuardRules, ...)
 * does load performanceApi and baseApi; importing this module alone does not.
 */
export const PERF_NAV_IDS = {
  PERF_HOME: "perf-home",
  PERF_COACHING: "perf-coaching",
  PERF_TODOS: "perf-todos",
  PERF_PLANS: "perf-plans",
  PERF_EVALUATIONS: "perf-evaluations",
  PERF_VISITS: "perf-visits",
  PERF_ORG: "perf-org",
  PERF_RECORDS: "perf-records",
  PERF_DOCUMENTS: "perf-documents",
  PERF_FILE: "perf-file",
  /**
   * D5: the one Settings item, last, opening the tiled Settings page (`/performance/settings`,
   * exactly). Drawn whenever it has an entry: HR's tabbed page and Users, a user manager's Users,
   * a Skypoint staff member's Products.
   */
  PERF_SETTINGS: "perf-settings",
  /** E9: the tabbed Settings page (`/performance/settings/:tab`), an entry of PERF_SETTINGS (HR). */
  PERF_CONFIG: "perf-config",
} as const;
export type PerfNavId = (typeof PERF_NAV_IDS)[keyof typeof PERF_NAV_IDS];

/** The hire Users page is shared by both products (spec §6: "the shared Settings items"). */
const SHARED_USERS_NAV_ID = "access-management";

/**
 * Feature pages that exist in this release. Each Phase 3 epic appends its id when its page ships
 * (E1: Coaching). The other Phase 3 routes in ROUTES are placeholders with no page mounted, so
 * they are never drawn and the guard never admits them.
 */
export const SHIPPED_FEATURES: readonly PerfNavId[] = [
  PERF_NAV_IDS.PERF_COACHING,
  // E2: My file (D7); /performance/file/:personKey belongs to it too.
  PERF_NAV_IDS.PERF_FILE,
  // E6: To-dos; /performance/todos/:tab belongs to it too.
  PERF_NAV_IDS.PERF_TODOS,
  // E7: the Org chart (managers and HR; O-4 took it from the employee, superseding D1).
  PERF_NAV_IDS.PERF_ORG,
  // E3: Plans (managers and HR; the employee's role keeps showPerfPlans off, O-1 / R4).
  PERF_NAV_IDS.PERF_PLANS,
  // E4: Evaluations (managers and HR; the employee's role keeps showPerfEvaluations off and opens
  // their own from Home and their file).
  PERF_NAV_IDS.PERF_EVALUATIONS,
  // E5: Site visits, where the pack's visits apply and the caller sees them (core.js:155,257).
  PERF_NAV_IDS.PERF_VISITS,
  // E8: Records (managers and HR) and the documents item ("My documents" for an employee; for a
  // manager and HR it is Records' Documents tab, 2026-10-23).
  PERF_NAV_IDS.PERF_RECORDS,
  PERF_NAV_IDS.PERF_DOCUMENTS,
];

export interface PerformanceNavContext {
  navigation: Record<string, boolean>;
  /** False when the vertical has no site visits (`visits.runMinLevel === null`). */
  visitsApply: boolean;
  /**
   * E5 (wireframe `core.js:155`): false for a manager below both the level that runs visits and
   * the level that sees them; the Visits item is then not drawn. Absent means true.
   */
  seesVisits?: boolean;
  /** Hire users.manage or performance.users.manage on this instance. */
  canManageUsers: boolean;
  /**
   * performance.users.manage on this instance (perf_hr). The Settings page is drawn only with it,
   * as its routes admit only perf_hr (GET/PUT /positions and /settings, no hire fallback); the
   * showPerfSettings switch alone is not enough. Absent means false.
   */
  managesPerformanceUsers?: boolean;
  /** Feature pages that exist in this release. */
  features: readonly PerfNavId[];
  terms?: Partial<Record<TermKey, string>>;
  /**
   * E6: whose to-do list this is (wireframe `core.js:229-253`): the employee's item reads
   * "My to-dos", HR's sits second. Absent: "To-dos" in the shared order.
   */
  todosRole?: "employee" | "manager" | "hr";
  /** E6: the caller's own open to-dos, drawn as the item's count when above zero (`core.js:270`). */
  openTodos?: number;
  /** E3: plans waiting on the caller's decision, drawn as the Plans item's count (`core.js:271`). */
  plansWaiting?: number;
  /**
   * WF-NAV-1: records about an employee still waiting on their acknowledgement, drawn as "My
   * <coaching>"'s count when above zero (`core.js:272`). The employee's item only.
   */
  coachingWaiting?: number;
  /**
   * The caller's performance role name (my-permissions' `role`; null or absent with no role here).
   * E7 O-4: perf_employee draws exactly the wireframe's employee nav (core.js:231-237): "My
   * <coaching>", and no Settings group or shared Users entry even when the same account holds a
   * hire admin role (that admin manages users from Hire). The role name, not todosRole, which
   * reads "employee" for a hire admin with no performance role (an empty permission map).
   */
  performanceRole?: string | null;
}

const plural = (word: string) => (/s$/i.test(word) ? word : `${word}s`);

interface FeatureItem {
  id: PerfNavId;
  navKey: string;
  label: (t: Record<TermKey, string>) => string;
  icon: NavItem["icon"];
  needsVisits?: boolean;
}

const FEATURE_ITEMS: readonly FeatureItem[] = [
  {
    id: PERF_NAV_IDS.PERF_COACHING,
    navKey: "showPerfCoaching",
    label: (t) => t.coaching,
    icon: MessageSquareText,
  },
  {
    id: PERF_NAV_IDS.PERF_TODOS,
    navKey: "showPerfTodos",
    // The employee's label is set in buildPerformanceNavSections ("My to-dos").
    label: () => "To-dos",
    icon: ListChecks,
  },
  {
    // E2 D7: after To-dos, as the wireframe's employee nav (core.js:231-236).
    id: PERF_NAV_IDS.PERF_FILE,
    navKey: "showPerfFile",
    label: () => "My file",
    icon: FolderOpen,
  },
  {
    id: PERF_NAV_IDS.PERF_PLANS,
    navKey: "showPerfPlans",
    // The wireframe's plural of the short term ("PIPs", core.js:241,254); renamed terms follow.
    label: (t) => plural(t.pipShort),
    icon: ClipboardCheck,
  },
  {
    id: PERF_NAV_IDS.PERF_EVALUATIONS,
    navKey: "showPerfEvaluations",
    label: () => "Evaluations",
    icon: ClipboardList,
  },
  {
    id: PERF_NAV_IDS.PERF_VISITS,
    navKey: "showPerfVisits",
    label: (t) => plural(t.visit),
    icon: Building2,
    needsVisits: true,
  },
  {
    id: PERF_NAV_IDS.PERF_ORG,
    navKey: "showPerfOrg",
    label: () => "Org chart",
    icon: Network,
  },
  {
    id: PERF_NAV_IDS.PERF_RECORDS,
    navKey: "showPerfRecords",
    label: () => "Records",
    icon: Folder,
  },
  {
    id: PERF_NAV_IDS.PERF_DOCUMENTS,
    navKey: "showPerfDocuments",
    label: () => "Document Centre",
    icon: FileText,
  },
];

export function buildPerformanceNavSections(ctx: PerformanceNavContext): NavSection[] {
  const terms: Record<TermKey, string> = {
    ...BASE_PACK.terms,
    ...(ctx.terms ?? {}),
  };
  const on = (key: string) => ctx.navigation[key] === true;
  const employee = ctx.performanceRole === PERFORMANCE_ROLE_NAMES.employee;
  const items: NavItem[] = [{ id: PERF_NAV_IDS.PERF_HOME, label: "Home", icon: Home }];
  for (const f of FEATURE_ITEMS) {
    if (!ctx.features.includes(f.id) || !on(f.navKey)) continue;
    if (f.needsVisits && (!ctx.visitsApply || ctx.seesVisits === false)) continue;
    if (f.id === PERF_NAV_IDS.PERF_TODOS) {
      const todos: NavItem = {
        id: f.id,
        label: ctx.todosRole === "employee" ? "My to-dos" : f.label(terms),
        icon: f.icon,
        ...(ctx.openTodos && ctx.openTodos > 0 ? { badge: ctx.openTodos } : {}),
      };
      // HR's list is second, right after Home (wireframe core.js:238-240).
      if (ctx.todosRole === "hr") items.splice(1, 0, todos);
      else items.push(todos);
      continue;
    }
    if (f.id === PERF_NAV_IDS.PERF_PLANS) {
      const plans: NavItem = {
        id: f.id,
        label: f.label(terms),
        icon: f.icon,
        ...(ctx.plansWaiting && ctx.plansWaiting > 0 ? { badge: ctx.plansWaiting } : {}),
      };
      // HR's Plans is third, right after To-dos (wireframe core.js:238-241).
      if (ctx.todosRole === "hr") items.splice(2, 0, plans);
      else items.push(plans);
      continue;
    }
    if (f.id === PERF_NAV_IDS.PERF_EVALUATIONS) {
      const evaluations: NavItem = { id: f.id, label: f.label(terms), icon: f.icon };
      // HR's Evaluations is fourth, right after PIPs (wireframe core.js:238-242); a manager's
      // follows PIPs in order (core.js:249-254).
      if (ctx.todosRole === "hr") {
        const after = items.findIndex((i) => i.id === PERF_NAV_IDS.PERF_PLANS);
        items.splice(after >= 0 ? after + 1 : items.length, 0, evaluations);
      } else items.push(evaluations);
      continue;
    }
    // WF-NAV-4: the wireframe's employee nav (core.js:231-237) has no Records, and its documents
    // item is "My documents". The role's switches are on for E8, so the nav holds the line.
    if (employee && f.id === PERF_NAV_IDS.PERF_RECORDS) continue;
    if (employee && f.id === PERF_NAV_IDS.PERF_DOCUMENTS) {
      items.push({ id: f.id, label: "My documents", icon: f.icon });
      continue;
    }
    // Approved design 2026-10-23: for a manager and HR the Document Centre is Records' Documents
    // tab, so it is not an item of its own. A role drawing documents but not Records keeps it.
    if (
      f.id === PERF_NAV_IDS.PERF_DOCUMENTS &&
      items.some((i) => i.id === PERF_NAV_IDS.PERF_RECORDS)
    )
      continue;
    // E7 O-4: the employee's coaching item is the wireframe's 'My ' + coaching (core.js:233), with
    // the records waiting on them as its count (WF-NAV-1, core.js:272).
    if (employee && f.id === PERF_NAV_IDS.PERF_COACHING) {
      items.push({
        id: f.id,
        label: `My ${inSentence(terms.coaching)}`,
        icon: f.icon,
        ...(ctx.coachingWaiting && ctx.coachingWaiting > 0 ? { badge: ctx.coachingWaiting } : {}),
      });
      continue;
    }
    items.push({ id: f.id, label: f.label(terms), icon: f.icon });
  }
  // E9 O-2 and D5: one Settings item, last, whose children are its entries (the tiles).
  // The tabbed page is HR's alone (its routes admit only perf_hr).
  const hrSettings = on("showPerfSettings") && ctx.managesPerformanceUsers === true;
  // Operator decision 2026-10-06 (wireframe core.js:238-262): in the Performance product only HR
  // has Settings. A manager or employee who is also a hire admin manages users from Hire, so they
  // get no Users entry here. Kept for a caller with no performance role (a hire user manager).
  const managesUsersHere = hrSettings || (ctx.canManageUsers && !employee && !ctx.performanceRole);
  const entries: NavItem[] = [];
  if (hrSettings) entries.push({ id: PERF_NAV_IDS.PERF_CONFIG, label: "Settings", icon: Settings });
  if (managesUsersHere) entries.push({ id: SHARED_USERS_NAV_ID, label: "Users", icon: UserCog });
  if (entries.length > 0) {
    items.push({
      id: PERF_NAV_IDS.PERF_SETTINGS,
      label: "Settings",
      icon: Settings,
      children: entries,
    });
  }
  return [{ header: "Performance", items }];
}
